import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  ITicketSoporteCliente,
  ITicketSoporteClientePagingRes,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_TICKET_SOPORTE_PAGING,
  MOCK_TICKET_SOPORTE_LEGACY,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTicketSoporteCliente = ({
  page = 1,
  page_size = 10,
  enabled = true,
  ...params
}: GetTicketSoporteClienteParams) => {
  return useQuery({
    queryKey: ['ticketsoporte', page, page_size, params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_TICKET_SOPORTE_PAGING);
      return getTicketSoporteClientes({ page, page_size, ...params });
    },
    enabled,
  });
};

export const useFetchTicketSoporteClienteEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetTicketSoporteClienteParams>) => {
  return useQuery({
    queryKey: ['ticketsoporte', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_TICKET_SOPORTE_PAGING);
      return getTicketSoporteClientes(params);
    },
    enabled,
  });
};

export const useGetTicketSoporteCliente = (id: string) => {
  return useQuery({
    queryKey: ['ticketsoportes', id],
    queryFn: async () => {
      if (USE_MOCK) {
        const ticket = MOCK_TICKET_SOPORTE_LEGACY.find((t) => t.id_ticket_soporte === id) || MOCK_TICKET_SOPORTE_LEGACY[0];
        return mockAxiosResponse(ticket);
      }
      return getTicketSoporteCliente(id);
    },
  });
};

export const useCreateTicketSoporteCliente = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTicketSoporteCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticketsoporte'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ticket de soporte creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTicketSoporteCliente = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateTicketSoporteCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticketsoporte'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ??
            'Ticket de soporte actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetTicketSoporteClienteParams = Partial<ITicketSoporteCliente> & {
  page?: number;
  page_size?: number;
  razon_social?: string;
  identificacion?: string;
  codigo?: string;
  enabled?: boolean;
};

export type CreateTicketSoporteClienteParams = Omit<
  ITicketSoporteCliente,
  'id_ticket_soporte'
>;
export type UpdateTicketSoporteClienteParams = {
  id: string;
  data: CreateTicketSoporteClienteParams;
};

export const getTicketSoporteClientes = async (
  params?: GetTicketSoporteClienteParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ITicketSoporteClientePagingRes>(
    `${urlBackend}api/v1/ticketsoporte/?${queryParams}`,
    true
  );
};

export const getTicketSoporteCliente = async (id: string) => {
  try {
    return await get<ITicketSoporteCliente>(
      `${urlBackend}api/v1/ticketsoporte/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createTicketSoporteCliente = async (
  data: CreateTicketSoporteClienteParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ITicketSoporteCliente>(
    `${urlBackend}api/v1/ticketsoporte/`,
    data,
    true
  );
};
export const updateTicketSoporteCliente = async ({
  id,
  data,
}: UpdateTicketSoporteClienteParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ITicketSoporteCliente>(
    `${urlBackend}api/v1/ticketsoporte/${id}/`,
    data,
    true
  );
};
